import React, { useState, useEffect } from "react";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle, XCircle } from "lucide-react";
import { verifyEmail } from "@/services/authService";

const VerifyRegistration = ({ code }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!code) {
      setError("No verification code provided.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const verify = async () => {
      try {
        await verifyEmail(code);
        if (!cancelled) {
          setSuccess(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.message ||
              "Verification failed. The code may be invalid or expired.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [code]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Verifying Your Email...</CardTitle>
            <CardDescription>
              Please wait while we verify your registration.
            </CardDescription>
          </CardHeader>
          <div className="flex justify-center pb-6">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              <CardTitle>Email Verified</CardTitle>
            </div>
            <CardDescription>
              Your email has been verified successfully. You can now log in to
              your account and set up trip alerts.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            <CardTitle>Verification Failed</CardTitle>
          </div>
        </CardHeader>
        <div className="px-6 pb-6">
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </Card>
    </div>
  );
};

export default VerifyRegistration;
